import colors from '@Common/assets/colors';
import Table from '@Common/Table';
import { useGeneratorContext } from '@Context/GeneratorContext';
import { FeatItem } from '@Models/types';
import { MagnifyingGlass, SquaresFour, X } from '@phosphor-icons/react';
import { useEffect, useState } from 'react';
import { BounceLoader, MoonLoader } from 'react-spinners';

const Features = () => {
  const {
    featItems,
    selectedProduct,
    fetchFeatItems,
    setSelectedFeatItem,
    genPageLoading,
    setGenPageLoading,
  } = useGeneratorContext();
  //! Now uses context from generatorContext.tsx fike to know if any components are loading
  //* just aliased the loading variable to genPageLoading since it is already defined in this file
  const loading = genPageLoading;
  const [search, setSearch] = useState('');
  const [selectedRow, setSelectedRow] = useState<FeatItem | null>(null);
  const [filteredSearch, setFilteredSearch] = useState<FeatItem[]>(featItems);

  useEffect(() => {
    setFilteredSearch(featItems);
  }, [featItems]);

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    const query = e.target.value;
    setSearch(query);
    setFilteredSearch(
      featItems.filter(
        (feat) =>
          feat.name.toLowerCase().includes(query.toLowerCase()) ||
          feat.reference_num.toLowerCase().includes(query.toLowerCase())
      )
    );
  };

  useEffect(() => {
    if (loading) {
      setSearch('');
    }
  }, [loading]);

  const clearSearch = () => {
    setSearch('');
    setFilteredSearch(featItems);
  };

  useEffect(() => {
    if (selectedProduct) {
      setGenPageLoading(true);
      fetchFeatItems(selectedProduct).then(() => setGenPageLoading(false));
    }
  }, [selectedProduct]);

  const columns: {
    header: string;
    accessor: keyof FeatItem;
    Cell?: (value: string, row: FeatItem) => React.ReactNode;
  }[] = [
    { header: 'Name', accessor: 'name' },
    {
      header: 'Reference',
      accessor: 'reference_num',
      Cell: (value: string) => (
        <span className="whitespace-nowrap font-bold">{value}</span>
      ), // Custom cell renderer
    },
  ];

  const handleSelectFeature = (feature: FeatItem) => {
    setSelectedRow(feature);
    setSelectedFeatItem(feature);
  };

  return (
    <div
      data-testid="test-features"
      className="flex h-full flex-col rounded-lg bg-white"
    >
      <span className="flex w-full flex-col items-start justify-between gap-y-2 rounded-t-md bg-primary-700 px-4 py-2 sm:flex-row">
        <span className="font-extrabold text-white">Features</span>

        <div className="flex w-full flex-row items-center gap-x-2 rounded-md border bg-white px-2 py-1 sm:w-56">
          <MagnifyingGlass size={20} weight="bold" />
          <input
            disabled={loading}
            type="text"
            className={`w-full border-gray-200 font-inter text-sm focus:outline-none ${loading && 'animate-pulse'}`}
            placeholder={
              loading ? 'Loading Aha! Features' : 'Search Aha! Feature'
            }
            value={search}
            onChange={handleSearch}
          />

          {loading ? (
            <BounceLoader
              data-testid="test-feature-search-loading"
              size={20}
              color={colors.brandWarning[500]}
            />
          ) : (
            <X
              data-testid="test-clear-btn"
              weight="bold"
              className="cursor-pointer hover:text-brandRed-500"
              onClick={clearSearch}
            />
          )}
        </div>
      </span>

      <div className="mx-2 mb-2 flex h-0 flex-grow flex-col">
        {/* NO FEATURE */}
        {!selectedProduct && (
          <span className="flex h-full flex-col items-center justify-center text-neutral-300">
            <SquaresFour size={100} weight="bold" />
            <label className="text-xs text-neutral-500">
              Select a pod first
            </label>
          </span>
        )}

        {!loading && selectedProduct && featItems.length === 0 && (
          <span className="flex h-full flex-col items-center justify-center text-neutral-300">
            <SquaresFour size={100} weight="bold" />
            <label className="text-xs text-neutral-500">
              No Features Available
            </label>
          </span>
        )}

        {loading && selectedProduct ? (
          <span className="flex h-full w-full flex-col items-center justify-center sm:gap-y-6 sm:p-5">
            <MoonLoader color="#071D49" />
            <span className="animate-pulse text-xs font-semibold tracking-wide text-gray-600 sm:text-sm">
              Aha! Features Loading
            </span>
          </span>
        ) : (
          <>
            {filteredSearch && filteredSearch.length > 0 && (
              // CUSTOM TABLE
              <Table
                columns={columns}
                data={filteredSearch}
                selectedRow={selectedRow}
                rowClickHandler={handleSelectFeature}
              />
            )}
            {selectedProduct &&
              !loading &&
              featItems.length > 0 &&
              filteredSearch.length === 0 && (
                <span className="flex h-full flex-col items-center justify-center text-neutral-300">
                  <SquaresFour size={100} weight="bold" />
                  <label className="text-xs text-neutral-500">
                    No Features Found
                  </label>
                </span>
              )}
          </>
        )}
      </div>
    </div>
  );
};

export default Features;
